// Full-screen sign-in gate. Renders the Google Identity Services button and
// routes the returned credential through the AuthProvider.
import { useEffect, useRef, useState } from "react";
import { loadGis, useAuth, GOOGLE_CLIENT_ID } from "@/state/auth";

export function SignIn() {
  const { handleCredential, devSignIn, error } = useAuth();
  const btnRef = useRef<HTMLDivElement>(null);
  const [devEmail, setDevEmail] = useState("alan@shopfillpro.io");

  useEffect(() => {
    let cancelled = false;
    loadGis()
      .then((google) => {
        if (cancelled || !btnRef.current) return;
        google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (resp: { credential: string }) => handleCredential(resp.credential),
        });
        google.accounts.id.renderButton(btnRef.current, {
          theme: "filled_blue",
          size: "large",
          text: "signin_with",
          shape: "pill",
          width: 260,
        });
      })
      .catch(() => {
        /* GIS failed to load — the error text below covers it */
      });
    return () => {
      cancelled = true;
    };
  }, [handleCredential]);

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-8 text-center shadow-lg">
        <div className="font-display text-2xl font-semibold tracking-tight text-foreground">
          Flowio
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          Sign in to continue.
        </p>
        <div className="mt-6 flex justify-center">
          <div ref={btnRef} />
        </div>
        {error && (
          <p className="mt-4 text-sm text-destructive" role="alert">
            {error}
          </p>
        )}
        <p className="mt-6 text-xs text-muted-foreground">
          New here? Sign up with Google to create your agency workspace.
        </p>
        {import.meta.env.DEV && (
          <div className="mt-6 border-t border-border pt-4 text-left">
            <p className="text-xs font-medium text-muted-foreground">Local dev sign-in</p>
            <div className="mt-2 flex gap-2">
              <input
                type="email"
                value={devEmail}
                onChange={(e) => setDevEmail(e.target.value)}
                className="flex-1 rounded-md border border-border bg-background px-2 py-1 text-sm"
                placeholder="allowlisted email"
              />
              <button
                type="button"
                onClick={() => devSignIn(devEmail || undefined)}
                className="rounded-md bg-secondary px-3 py-1 text-sm font-medium text-secondary-foreground hover:bg-secondary/80"
              >
                Sign in
              </button>
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">
              Requires <code>DEV_LOGIN=1</code> and <code>SESSION_SECRET</code> in <code>.dev.vars</code>.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
